const ROUTE_ACTION_CAPABILITIES = {
    detail: 'view',
    create: 'create',
    list: 'list',
    log: 'log',
    logData: 'log',
    delete: 'delete',
    download: 'export',
    notify: 'notification',
};

const getRouteTokenCandidates = (drawerRoute) => {
    if (!drawerRoute || typeof drawerRoute !== 'string') return [];
    const tokens = drawerRoute
        .split('/')
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean);
    const baseToken = tokens[tokens.length - 1];
    if (!baseToken) return [];

    const candidates = new Set([baseToken]);
    if (baseToken.endsWith('ies') && baseToken.length > 3) {
        candidates.add(`${baseToken.slice(0, -3)}y`);
    }
    if (baseToken.endsWith('s') && baseToken.length > 1) {
        candidates.add(baseToken.slice(0, -1));
    } else {
        candidates.add(`${baseToken}s`);
        if (baseToken.endsWith('y') && baseToken.length > 1) {
            candidates.add(`${baseToken.slice(0, -1)}ies`);
        }
    }

    return Array.from(candidates);
};

const getTokenFromModulePath = (modulePath) => {
    if (!modulePath || typeof modulePath !== 'string') return null;
    const tokens = modulePath
        .split('/')
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean);
    return tokens[tokens.length - 1] ?? null;
};

const getNamespaceFromModulePath = (modulePath) => {
    if (!modulePath || typeof modulePath !== 'string') return null;
    return modulePath.split('/')[0]?.trim().toLowerCase() ?? null;
};

const pickByRouteToken = (entries, routeCandidates, pickKey) => {
    for (const routeToken of routeCandidates) {
        const match = entries.find((entry) => getTokenFromModulePath(pickKey(entry)) === routeToken);
        if (match) return match;
    }
    return null;
};

export const resolveDrawerDataProviderName = (routeName, params, existingModules) => {
    if (params?.dataProviderName) return params.dataProviderName;
    if (params?.model) return params.model;

    const modulePart = routeName?.split('/')?.[0];
    if (!modulePart) return null;

    const routeCandidates = getRouteTokenCandidates(modulePart);
    if (!routeCandidates.length) return null;
    if (!existingModules || typeof existingModules !== 'object') return null;

    const entries = Object.entries(existingModules).filter(([, config]) => config && typeof config === 'object');
    const normalizedModulePart = modulePart.toLowerCase();

    // Drawer module name is the namespace (e.g. cars/detail -> cars/car, not calendars/car).
    const namespaceEntries = entries.filter(([moduleName]) => getNamespaceFromModulePath(moduleName) === normalizedModulePart);
    if (namespaceEntries.length === 1) {
        return namespaceEntries[0][0];
    }
    if (namespaceEntries.length > 1) {
        const byNamespaceToken = pickByRouteToken(namespaceEntries, routeCandidates, ([moduleName]) => moduleName);
        if (byNamespaceToken) return byNamespaceToken[0];
        return namespaceEntries[0][0];
    }

    const byModuleToken = pickByRouteToken(entries, routeCandidates, ([moduleName]) => moduleName);
    if (byModuleToken) return byModuleToken[0];

    const byModelName = pickByRouteToken(entries, routeCandidates, ([, config]) => config?.modelName);
    if (byModelName) return byModelName[0];

    return null;
};

export const resolveDrawerRequiredCapability = (routeName, params, existingModules) => {
    if (params?.requiredCapability) return params.requiredCapability;

    const action = routeName?.includes('/')
        ? routeName.split('/').slice(1).join('/')
        : routeName;

    const knownSuffix = ROUTE_ACTION_CAPABILITIES[action];
    if (!knownSuffix) {
        // Wizard/associator routes: capability is enforced inside the drawer component (canSave).
        return null;
    }

    const dataProviderName = resolveDrawerDataProviderName(routeName, params, existingModules);
    if (!dataProviderName) return null;

    return `${dataProviderName}.${knownSuffix}`;
};

export const getMissingDrawerPermission = (routeName, params, userCapabilities, existingModules) => {
    const requiredCapability = resolveDrawerRequiredCapability(routeName, params, existingModules);
    if (!requiredCapability) return null;
    if (userCapabilities?.[requiredCapability]) return null;
    return requiredCapability;
};
